import type { FC } from 'react';
import { Inline } from '@george-labs.com/design-system';

import type { Amount } from '@shared/core-types';
import { t } from '@luna/i18n';
import { ConditionalWrapper } from '@luna/utils-ui';

import { LocalizedAmount } from '../localized-amount/LocalizedAmount';
import { isPermitted } from './PermittedAmountService';

type LocalizedAmountProps = Parameters<typeof LocalizedAmount>[0];

type Props = {
    flags?: string[];
    permissionFlags?: string[];
    smallText?: true;
    isBalanceAvailable?: boolean;
} & LocalizedAmountProps;

export const PermittedAmount: FC<Props> = ({
    flags = [],
    permissionFlags = [],
    smallText = false,
    amount,
    isBalanceAvailable = true,
    ...passThroughProps
}) => {
    const displayAmount = amount ?? ({ value: 0 } as Amount);

    if (!isPermitted(flags, permissionFlags)) {
        return (
            <ConditionalWrapper
                condition={smallText}
                wrapper={Inline}
                wrapperProps={{ small: true }}
            >
                <LocalizedAmount amount={displayAmount} {...passThroughProps} useDemoAmount />
            </ConditionalWrapper>
        );
    }

    if (!isBalanceAvailable) {
        return (
            <ConditionalWrapper
                condition={smallText}
                wrapper={Inline}
                wrapperProps={{ small: true }}
            >
                <LocalizedAmount
                    fallbackText={t('account.balance.notAvailable')}
                    data-cy="amount-not-available"
                />
            </ConditionalWrapper>
        );
    }

    return <LocalizedAmount amount={displayAmount} {...passThroughProps} />;
};
